/**
 * Complete Data Normalization System
 * 
 * This module provides comprehensive normalization functions to ensure
 * all data objects have complete schemas, preventing undefined property errors.
 */

/**
 * Default context definitions
 */
export const DEFAULT_CONTEXT_DEFINITIONS = {
  "1": "article",
  "2": "homepage", 
  "3": "category"
};

/**
 * Normalize site object with all expected properties
 */
export function normalizeSite(site = {}) {
  return {
    id: site.id || site.siteId || '',
    siteId: site.siteId || site.id || '',
    name: site.name || site.siteName || '',
    siteName: site.siteName || site.name || '',
    domain: site.domain || '',
    status: site.status || 'active',
    currency: site.currency || 'EUR',
    prebidTimeout: site.prebidTimeout || 1000,
    debugMode: site.debugMode || false,
    contextDefinitions: site.contextDefinitions || DEFAULT_CONTEXT_DEFINITIONS,
    ...site // Preserve any additional properties
  };
}

/**
 * Normalize placement context with all expected properties
 */
export function normalizeContext(context = {}) {
  return {
    enabled: context.enabled || false,
    sizes: {
      desktop: Array.isArray(context.sizes?.desktop) ? context.sizes.desktop : [[300, 250]],
      mobile: Array.isArray(context.sizes?.mobile) ? context.sizes.mobile : [[300, 250]]
    },
    gamPath: context.gamPath || '',
    bidderParams: typeof context.bidderParams === 'object' ? context.bidderParams : {},
    ...context // Preserve any additional properties
  };
}

/**
 * Normalize video settings with all expected properties
 */
export function normalizeVideoSettings(videoSettings = {}) {
  return {
    context: videoSettings.context || 'outstream',
    playerSize: Array.isArray(videoSettings.playerSize) ? videoSettings.playerSize : [[640, 360]],
    mimes: Array.isArray(videoSettings.mimes) ? videoSettings.mimes : ['video/mp4'],
    protocols: Array.isArray(videoSettings.protocols) ? videoSettings.protocols : [2, 3],
    playbackmethod: Array.isArray(videoSettings.playbackmethod) ? videoSettings.playbackmethod : [6],
    minduration: videoSettings.minduration || 5,
    maxduration: videoSettings.maxduration || 30,
    startdelay: videoSettings.startdelay || 0,
    placement: videoSettings.placement || 2, // in-article
    linearity: videoSettings.linearity || 1, // linear
    skip: videoSettings.skip || 0,
    skipmin: videoSettings.skipmin || 0,
    skipafter: videoSettings.skipafter || 0,
    api: Array.isArray(videoSettings.api) ? videoSettings.api : [1, 2],
    renderer: {
      url: videoSettings.renderer?.url || 'https://cdn.jsdelivr.net/npm/in-renderer-js@1/dist/in-renderer.umd.min.js',
      autoPlay: videoSettings.renderer?.autoPlay !== undefined ? videoSettings.renderer.autoPlay : true,
      mute: videoSettings.renderer?.mute !== undefined ? videoSettings.renderer.mute : true,
      adText: videoSettings.renderer?.adText || 'Advertisement'
    },
    ...videoSettings // Preserve any additional properties
  };
}

/**
 * Normalize placement settings with all expected properties
 */
export function normalizeSettings(settings = {}) {
  return {
    lazyLoad: settings.lazyLoad || false,
    refresh: settings.refresh || false,
    fluid: settings.fluid || false,
    mediaType: settings.mediaType || 'banner', // banner, video, or both
    lazyLoadMargin: {
      desktop: settings.lazyLoadMargin?.desktop || '800px',
      mobile: settings.lazyLoadMargin?.mobile || '600px'
    },
    refreshInterval: settings.refreshInterval || 30000,
    deviceTargeting: Array.isArray(settings.deviceTargeting) ? settings.deviceTargeting : ['desktop', 'mobile'],
    videoSettings: settings.mediaType === 'video' || settings.mediaType === 'both' 
      ? normalizeVideoSettings(settings.videoSettings || {}) 
      : null,
    ...settings // Preserve any additional properties
  };
}

/**
 * Normalize bidder parameters for specific bidders
 */
export function normalizeBidderParams(bidderName, params = {}) {
  const defaults = {
    teads: {
      placementId: 0,
      pageId: 0
    },
    criteo: {
      networkId: 0
    },
    triplelift: {
      inventoryCode: ''
    },
    sovrn: {
      tagid: ''
    },
    smartadserver: {
      formatId: ''
    }
  };

  const bidderDefaults = defaults[bidderName.toLowerCase()] || {};
  
  return {
    ...bidderDefaults,
    ...params
  };
}

/**
 * Normalize placement object with all expected properties
 */
export function normalizePlacement(placement = {}, placementName = '') {
  const normalizedContexts = {};
  
  // Normalize all contexts
  if (placement.contexts && typeof placement.contexts === 'object') {
    for (const [contextId, context] of Object.entries(placement.contexts)) {
      if (context && typeof context === 'object') {
        normalizedContexts[contextId] = normalizeContext(context);
        
        // Normalize bidder params within context
        if (normalizedContexts[contextId].bidderParams) {
          const normalizedBidderParams = {};
          for (const [bidderName, params] of Object.entries(normalizedContexts[contextId].bidderParams)) {
            normalizedBidderParams[bidderName] = normalizeBidderParams(bidderName, params);
          }
          normalizedContexts[contextId].bidderParams = normalizedBidderParams;
        }
      }
    }
  }

  return {
    selector: placement.selector || `#${placementName}`,
    contexts: normalizedContexts,
    settings: normalizeSettings(placement.settings),
    ...placement // Preserve any additional properties
  };
}

/**
 * Normalize floors configuration
 */
export function normalizeFloors(floors = {}) {
  return {
    version: floors.version || '2.0.0',
    site: floors.site || '',
    enabled: floors.enabled || false,
    currency: floors.currency || 'EUR',
    default: floors.default || 0.25,
    overrides: floors.overrides && typeof floors.overrides === 'object' ? floors.overrides : {},
    bidderFloors: floors.bidderFloors && typeof floors.bidderFloors === 'object' ? floors.bidderFloors : {},
    rules: {
      skipRate: floors.rules?.skipRate || 0.1,
      modelVersion: floors.rules?.modelVersion || 'v2.0',
      enforcement: floors.rules?.enforcement || 'soft'
    },
    lastUpdated: floors.lastUpdated || new Date().toISOString(),
    notes: {
      structure: floors.notes?.structure || '',
      default: floors.notes?.default || '',
      ...floors.notes
    },
    ...floors // Preserve any additional properties
  };
}

/**
 * Normalize bidder configuration
 */
export function normalizeBidder(bidder = {}) {
  return {
    enabled: bidder.enabled || false,
    networkId: bidder.networkId || 0,
    siteId: bidder.siteId || 0,
    pageId: bidder.pageId || 0,
    ...bidder // Preserve any additional properties
  };
}

/**
 * Normalize user ID module configuration
 */
export function normalizeUserIdModule(module = {}) {
  return {
    enabled: module.enabled || false,
    expiry: module.expiry || 30,
    pubId: module.pubId || 0,
    ...module // Preserve any additional properties
  };
}

/**
 * Normalize prebid build configuration
 */
export function normalizePrebidBuild(build = {}) {
  return {
    enabledBidders: Array.isArray(build.enabledBidders) ? build.enabledBidders : [],
    coreModules: Array.isArray(build.coreModules) ? build.coreModules : ['priceFloors', 'consentManagement', 'currency', 'userId'],
    userIdModules: Array.isArray(build.userIdModules) ? build.userIdModules : [],
    analyticsModules: Array.isArray(build.analyticsModules) ? build.analyticsModules : [],
    optimization: build.optimization || 'standard',
    version: build.version || '9.53.1',
    lastUpdated: build.lastUpdated || new Date().toISOString(),
    estimatedSize: build.estimatedSize || '~90KB',
    ...build // Preserve any additional properties
  };
}

/**
 * Normalize complete unified configuration
 */
export function normalizeConfig(config = {}) {
  const normalizedConfig = {
    site: normalizeSite(config.site),
    bidders: {},
    userIdModules: {},
    placements: {},
    floors: normalizeFloors(config.floors),
    modules: Array.isArray(config.modules) ? config.modules : ['priceFloors', 'consentManagement', 'currency', 'userId'],
    prebidBuild: normalizePrebidBuild(config.prebidBuild),
    lastUpdated: config.lastUpdated || new Date().toISOString()
  };

  // Normalize all bidders
  if (config.bidders && typeof config.bidders === 'object') {
    for (const [bidderName, bidder] of Object.entries(config.bidders)) {
      normalizedConfig.bidders[bidderName] = normalizeBidder(bidder);
    }
  }

  // Normalize all user ID modules
  if (config.userIdModules && typeof config.userIdModules === 'object') {
    for (const [moduleName, module] of Object.entries(config.userIdModules)) {
      normalizedConfig.userIdModules[moduleName] = normalizeUserIdModule(module);
    }
  }

  // Normalize all placements
  if (config.placements && typeof config.placements === 'object') {
    for (const [placementName, placement] of Object.entries(config.placements)) {
      if (placement && typeof placement === 'object') {
        normalizedConfig.placements[placementName] = normalizePlacement(placement, placementName);
      }
    }
  }

  return {
    ...normalizedConfig,
    ...config // Preserve any additional properties not handled above
  };
}

/**
 * Create new placement template with complete structure
 */
export function createPlacementTemplate(placementName, siteId = '', contextId = '1', mediaType = 'banner') {
  const template = {
    selector: `#${placementName}`,
    contexts: {
      [contextId]: {
        enabled: true,
        sizes: {
          desktop: mediaType === 'video' ? [[640, 360]] : [[300, 250]],
          mobile: mediaType === 'video' ? [[400, 300]] : [[300, 250]]
        },
        gamPath: `/22264327084/${siteId}_art/${placementName}`,
        bidderParams: {}
      }
    },
    settings: {
      lazyLoad: true,
      refresh: false,
      fluid: mediaType === 'video' ? false : true,
      mediaType: mediaType
    }
  };

  // Add video settings if media type is video or both
  if (mediaType === 'video' || mediaType === 'both') {
    template.settings.videoSettings = {
      context: 'outstream',
      playerSize: [[640, 360]],
      mimes: ['video/mp4'],
      protocols: [2, 3],
      playbackmethod: [6],
      minduration: 5,
      maxduration: 30,
      renderer: {
        url: 'https://cdn.jsdelivr.net/npm/in-renderer-js@1/dist/in-renderer.umd.min.js',
        autoPlay: true,
        mute: true,
        adText: 'Advertisement'
      }
    };
  }

  return normalizePlacement(template, placementName);
}

/**
 * Validate and normalize placement for React components
 * This is the main function components should use
 */
export function preparePlacementForEdit(placement, placementName = '') {
  if (!placement || typeof placement !== 'object') {
    console.warn(`Invalid placement data for ${placementName}, creating template`);
    return createPlacementTemplate(placementName);
  }

  try {
    const normalized = normalizePlacement(placement, placementName);
    
    // Additional safety validation to prevent property access errors
    if (normalized.contexts) {
      Object.entries(normalized.contexts).forEach(([contextId, context]) => {
        if (!context.sizes || typeof context.sizes !== 'object') {
          console.warn(`Context ${contextId} missing sizes, fixing...`);
          context.sizes = { desktop: [[300, 250]], mobile: [[300, 250]] };
        }
        if (!context.sizes.desktop || !Array.isArray(context.sizes.desktop)) {
          console.warn(`Context ${contextId} missing desktop sizes, fixing...`);
          context.sizes.desktop = [[300, 250]];
        }
        if (!context.sizes.mobile || !Array.isArray(context.sizes.mobile)) {
          console.warn(`Context ${contextId} missing mobile sizes, fixing...`);
          context.sizes.mobile = [[300, 250]];
        }
      });
    }
    
    // Ensure settings structure is complete
    if (!normalized.settings?.lazyLoadMargin) {
      console.warn(`Placement ${placementName} missing lazyLoadMargin, fixing...`);
      normalized.settings = normalized.settings || {};
      normalized.settings.lazyLoadMargin = { desktop: '800px', mobile: '600px' };
    }
    
    return normalized;
  } catch (error) {
    console.error(`Error normalizing placement ${placementName}:`, error);
    return createPlacementTemplate(placementName);
  }
}

/**
 * Normalize array of processed placement data for table display
 */
export function normalizePlacementData(placement = {}) {
  return {
    name: placement.name || '',
    type: placement.type || 'other',
    contexts: Array.isArray(placement.contexts) ? placement.contexts : [],
    desktopSizes: Array.isArray(placement.desktopSizes) ? placement.desktopSizes : [],
    mobileSizes: Array.isArray(placement.mobileSizes) ? placement.mobileSizes : [],
    bidders: Array.isArray(placement.bidders) ? placement.bidders : [],
    isActive: placement.isActive || false,
    data: placement.data && typeof placement.data === 'object' ? placement.data : {}
  };
}